/* globals process */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import GoogleMap from 'google-map-react';
import Helmet from 'react-helmet';
import { FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames';
import { autobind } from 'core-decorators';

import { placeReviews, newPlaceReview } from 'actions';
import { disableTouchScroll } from 'utils';
import { byIdShape } from 'utils/propTypes';

import PageHeader from 'AppComponents/PageHeader';
import Address from 'AppComponents/Address';
import PlaceMarker from 'AppComponents/MapMarker/Place';
import ChargerMarker from 'AppComponents/MapMarker/Charger';
import AverageRating from 'AppComponents/AverageRating';
import MapContainer from 'AppComponents/MapContainer';
import Reviews from 'AppComponents/Reviews';

import NotFound from 'AppViews/NotFound';

import ReviewForm from './components/ReviewForm';
import Review from './components/Review';

import './styles.scss';


class Place extends Component {

  static propTypes = {
    chargersById: byIdShape.isRequired,
    isLoggedIn: PropTypes.bool,
    newPlaceReview: PropTypes.func.isRequired,
    placeReviewsRequest: PropTypes.func.isRequired,
    placesById: byIdShape.isRequired,
    reviewsById: byIdShape.isRequired,
    routeParams: PropTypes.shape({ placeId: PropTypes.string }).isRequired,
    usersById: byIdShape.isRequired,
  };

  static defaultProps = {
    isLoggedIn: false,
  };

  static contextTypes = {
    intl: intlShape.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    const placeId = props.routeParams.placeId;
    const place = props.placesById[placeId];
    this.state = {
      showUpdateModal: false,
      map: {
        center: place && place.address && place.address.coords ? {
          lat: place.address.coords.coordinates[1],
          lng: place.address.coords.coordinates[0],
        } : { lat: 0, lng: 0 },
        zoom: 10,
        maxZoom: null,
        minZoom: null,
        maxFitZoom: 18,
        scrollwheel: false,
      },
    };
    this._changingBounds = false;
  }

  componentDidMount() {
    this.props.placeReviewsRequest(this.props.routeParams.placeId);
  }

  componentWillReceiveProps(nextProps) {
    const place = nextProps.placesById[nextProps.routeParams.placeId];
    const markers = [];
    if (place) {
      if (place.address && place.address.coords) {
        markers.push({
          props: {
            lat: place.address.coords.coordinates[1],
            lng: place.address.coords.coordinates[0],
          },
        });
      }
      if (place.chargers) {
        place.chargers.forEach((chargerId) => {
          const charger = nextProps.chargersById[chargerId];
          let chargerCoords = charger.address.coords.coordinates;
          if (charger.custom_coords) chargerCoords = charger.custom_coords.coordinates;
          markers.push({
            props: {
              lat: chargerCoords[1],
              lng: chargerCoords[0],
            },
          });
        });
      }
      this.getBoundsFromMarkers(markers);
    }
  }

  @autobind
  onGoogleLoaded({ map, maps }) {
    this.map = map;
    this.maps = maps;
    this.map.setMapTypeId(maps.MapTypeId.HYBRID);
    this.map.setTilt(0);
    this.getBoundsFromMarkers(this.markers);

    disableTouchScroll('map-container');
  }

  getBoundsFromMarkers(markers) {
    if (!this._changingBounds) {
      if (this.maps) {
        const bounds = new this.maps.LatLngBounds();
        markers.forEach((marker) => {
          const point = new this.maps.LatLng(marker.props.lat, marker.props.lng);
          bounds.extend(point);
        });
        this.map.setOptions({ maxZoom: this.state.map.maxFitZoom });
        this.map.fitBounds(bounds);
        this.setState({
          map: Object.assign({}, this.state.map, {
            center: this.map.getCenter().toJSON(),
            zoom: this.map.getZoom(),
          }),
        });
        this.map.setOptions({ maxZoom: this.state.map.maxZoom });
      }
    }
  }

  @autobind
  newReviewSuccess(review) {
    this.props.newPlaceReview(this.props.routeParams.placeId, review);
  }

  @autobind
  handleMapChange({ center, zoom }) {
    this._changingBounds = true;
    this.setState({
      map: Object.assign({}, this.state.map, {
        center,
        zoom,
      }),
    });
    this._changingBounds = false;
  }

  @autobind
  handleMapClick() {
    this.setState({
      map: Object.assign({}, this.state.map, {
        scrollwheel: !this.state.map.scrollwheel,
      }),
    }, () => {
      this.map.setOptions({ scrollwheel: this.state.map.scrollwheel });
    });
  }

  render() {
    const place = this.props.placesById[this.props.routeParams.placeId];

    if (!place) return <NotFound objectType='Place' />;

    const { isLoggedIn } = this.props;

    this.markers = [];

    const chargers = [];
    if (place.chargers) {
      place.chargers.forEach((chargerId) => {
        const charger = this.props.chargersById[chargerId];
        chargers.push(charger);
        let chargerCoords = charger.address.coords.coordinates;
        if (charger.custom_coords) chargerCoords = charger.custom_coords.coordinates;
        this.markers.push(
          <ChargerMarker
            charger={charger}
            key='charger'
            lat={chargerCoords[1]}
            lng={chargerCoords[0]}
          />,
        );
      });
    }

    const address = place.address;

    const mapOptions = {
      scrollwheel: this.state.map.scrollwheel,
    };
    if (this.state.map.maxZoom) mapOptions.maxZoom = this.state.map.maxZoom;
    if (this.state.map.minZoom) mapOptions.minZoom = this.state.map.minZoom;

    if (place.address && place.address.coords) {
      this.markers.push(
        <PlaceMarker
          place={place}
          key='place'
          lat={address.coords.coordinates[1]}
          lng={address.coords.coordinates[0]}
          allowHover={false}
          intl={this.context.intl}
        />,
      );
    }

    return (
      <div className='container'>

        <Helmet
          title={place.name}
          meta={[
            { name: 'description', content: `View and share reviews of ${place.name}` },
          ]}
        />

        <PageHeader title={place.name}>

          <small>
            <Address address={address} googleLink />

            { chargers.length > 0 ?
              <p>
                <FormattedMessage
                  id='placeDetail.chargersNear'
                  values={{ numChargers: chargers.length }}
                  defaultMessage='Nearby {numChargers, plural,
                    one {Charger}
                    other {Chargers}
                  }: '
                />
                { chargers.map((charger, i) => ([
                  <Link key={charger.pk} to={`/charger/${charger.pk}/`}>{ charger.title }</Link>,
                  <span>{ i !== chargers.length - 1 ? ', ' : '' }</span>,
                ])) }
              </p>
            : null }
          </small>

        </PageHeader>

        <section
          className={classNames(
            'place-details',
            place.average_rating ? 'has-rating' : 'no-rating',
            place.notes ? 'has-notes' : 'no-notes',
          )}
          styleName='place-details'
        >

          <div styleName='summary-and-meta'><div className='row'>

            <div styleName='summary'>

              <div styleName='content'>

                <div className='average-ratings'>

                  { place.average_rating ?
                    <AverageRating rating={place.average_rating} label='Average Rating' />
                  : null }

                  { place.average_walkability_rating ?
                    <AverageRating
                      rating={place.average_walkability_rating}
                      label='Average Walkability Rating'
                      type='walk'
                    />
                  : null }

                </div>

                <div
                  className='notes'
                  dangerouslySetInnerHTML={{ __html: place.notes }} // eslint-disable-line react/no-danger, max-len
                />

              </div>

            </div>

            <div styleName='meta'>

              <div styleName='content'>

                <ul styleName='contacts'>

                  { place.url ?
                    <li styleName='url'>
                      <a href={place.url} target='_blank' rel='noopener noreferrer'>
                        { place.url }
                      </a>
                    </li>
                  : null }

                  { place.tel ?
                    <li styleName='tel'>
                      <a href={`tel:${place.tel}`}>{ place.tel_natl || place.tel }</a>
                    </li>
                  : null }

                  { place.email ?
                    <li styleName='email'>
                      <a href={`mailto:${place.email}`}>{ place.email }</a>
                    </li>
                  : null }

                </ul>

              </div>

            </div>

          </div></div>

          <div styleName='location'>

            <div styleName='content'>

              <MapContainer
                scrollwheel={this.state.map.scrollwheel}
                styleName='map-container'
              >
                <GoogleMap
                  bootstrapURLKeys={{ key: process.env.GOOGLE_MAPS_API_KEY }}
                  center={this.state.map.center}
                  zoom={this.state.map.zoom}
                  options={mapOptions}
                  onChange={this.handleMapChange}
                  onClick={this.handleMapClick}
                  onGoogleApiLoaded={this.onGoogleLoaded}
                  yesIWantToUseGoogleMapApiInternals
                >
                  { this.markers }
                </GoogleMap>
              </MapContainer>

            </div>

          </div>

        </section>

        <Reviews
          canReview={isLoggedIn && place.user_review_status && place.user_review_status.can_review}
          isLoggedIn={this.props.isLoggedIn}
          loading={place.num_reviews > 0 && place.reviews === undefined}
          numReviews={place.num_reviews}
          reviewStatus={place.user_review_status}
          reviewForm={(
            <ReviewForm
              place={place}
              newReviewSuccess={this.newReviewSuccess}
              reviewStatus={place.user_review_status || {}}
              numReviews={place.num_reviews}
            />
          )}
        >
          { (place.reviews || []).map((id) => {
            const review = this.props.reviewsById[id];
            return (
              <Review
                key={id}
                review={review}
                user={this.props.usersById[review.user]}
              />
            );
          }) }
        </Reviews>

      </div>
    );
  }

}

const mapStateToProps = state => ({
  chargersById: state.chargers.byId,
  placesById: state.places.byId,
  reviewsById: state.places.reviewsById,
  usersById: state.users.byId,
  isLoggedIn: state.auth.isLoggedIn,
});

const placeReviewsRequest = placeReviews.request;

export default connect(
  mapStateToProps, {
    placeReviewsRequest,
    newPlaceReview,
  },
)(Place);



// WEBPACK FOOTER //
// ./app/scripts/views/App/views/Place/index.jsx